<template>
  <div class="mentions-tab">
    <!-- Loading -->
    <div v-if="loading" class="mentions-tab__loading">
      <Loading :loading="true" loadingText="Loading mentions..." />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="mentions-tab__error">
      <div class="mentions-tab__error-box">
        <span>{{ error }}</span>
        <button class="mentions-tab__retry" @click="retry">Retry</button>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="internalNotes.length === 0" class="mentions-tab__empty">
      <p>No mentions yet</p>
    </div>

    <!-- Content -->
    <div v-else class="mentions-tab__content">
      <!-- Internal Notes - 2-column grid -->
      <div class="mentions-tab__header">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1.5V8.25H8.25V12H1.5V1.5H12ZM12 0H1.5C0.675 0 0 0.675 0 1.5V12C0 12.825 0.675 13.5 1.5 13.5H9L13.5 9V1.5C13.5 0.675 12.825 0 12 0ZM6.75 8.25H3V6.75H6.75V8.25ZM10.5 5.25H3V3.75H10.5V5.25Z" fill="#004879"/>
        </svg>
        <span>Internal Notes</span>
      </div>
      <div class="mentions-tab__grid">
        <MentionItem
          v-for="item in internalNotes"
          :key="item.id"
          :item="item"
          type="internalNotes"
          :isExpanded="expandedId === item.id"
          :customerNameMap="customerNameMap"
          @expand="handleExpand"
          @jump-to-conversation="handleJumpToConversation"
        />
      </div>

      <!-- Pagination -->
      <div v-if="total > 0" class="mentions-tab__pagination">
        <Pagination
          :total_items="total"
          :current_page="page"
          :per_page="perPage"
          :per_page_options="[10, 20, 50]"
          item_label="Mentions"
          @update:current_page="changePage"
          @update:per_page="changePerPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MentionItem from './MentionItem.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { getJwtTokenCustomerList } from '@/api/internalNotes';

// Module-level cache
let cachedCustomerNameMap = null;
let cachedToken = null;

export default {
  name: 'MentionsTab',
  components: { MentionItem, Loading },
  data() {
    return {
      expandedId: null,
      customerNameMap: {},
    };
  },
  computed: {
    ...mapState('notifications', {
      chat: state => state.mentions.chat,
      internalNotes: state => state.mentions.internalNotes,
      loading: state => state.mentions.loading,
      error: state => state.mentions.error,
      page: state => state.mentions.page,
      perPage: state => state.mentions.perPage,
      total: state => state.mentions.total,
    }),
  },
  mounted() {
    this.loadCustomerNameMap();
    document.addEventListener('click', this.handleClickOutside, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  },
  methods: {
    retry() {
      this.$store.dispatch('notifications/fetchMentions');
    },
    changePage(p) {
      this.$store.commit('notifications/SET_MENTIONS_PAGE', p);
      this.$store.dispatch('notifications/fetchMentions');
    },
    changePerPage(val) {
      this.$store.commit('notifications/SET_MENTIONS_PER_PAGE', val);
      this.$store.commit('notifications/SET_MENTIONS_PAGE', 1);
      this.$store.dispatch('notifications/fetchMentions');
    },
    handleExpand(itemId) {
      this.expandedId = itemId;
    },
    handleClickOutside(event) {
      if (!this.expandedId) return;
      const clickedItem = event.target.closest('.mention-item');
      if (clickedItem) return;
      this.expandedId = null;
    },
    handleJumpToConversation({ patientId, noteId }) {
      this.$emit('jump-to-conversation', { patientId, noteId });
    },
    async loadCustomerNameMap() {
      const currentToken = sessionStorage.getItem('token');
      if (cachedCustomerNameMap && cachedToken === currentToken) {
        this.customerNameMap = cachedCustomerNameMap;
        return;
      }
      try {
        const response = await getJwtTokenCustomerList();
        const map = (response || []).reduce((acc, item) => ({ ...acc, ...item }), {});
        cachedCustomerNameMap = map;
        cachedToken = currentToken;
        this.customerNameMap = map;
      } catch (e) {
        console.error('Failed to load customer name map:', e);
      }
    },
  },
};
</script>

<style scoped>
.mentions-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

.mentions-tab::-webkit-scrollbar {
  width: 4px;
}

.mentions-tab::-webkit-scrollbar-track {
  background: transparent;
}

.mentions-tab::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.mentions-tab::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.mentions-tab__loading,
.mentions-tab__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 0;
  color: #999;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.mentions-tab__error-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FAEAEA;
  border: 1px solid #f5c6c6;
  border-radius: 8px;
  padding: 12px 16px;
  color: #D10000;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.mentions-tab__retry {
  margin-left: auto;
  background: none;
  border: 1px solid #D10000;
  border-radius: 4px;
  color: #D10000;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

.mentions-tab__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  background: #fff;
  border: 1px dashed #C4C4C4;
  border-radius: 10px;
  padding: 21px 20px;
}

.mentions-tab__header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  padding: 0 4px;
}

.mentions-tab__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  align-items: start;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

.mentions-tab__grid::-webkit-scrollbar {
  width: 4px;
}

.mentions-tab__grid::-webkit-scrollbar-track {
  background: transparent;
}

.mentions-tab__grid::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.mentions-tab__grid::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Pagination */
.mentions-tab__pagination {
  flex-shrink: 0;
  margin-top: auto;
}
</style>
